import { analyticsAllowed } from "./analytics-consent.js";
import { isConsentHoldTimeZone } from "./consent-geo.js";
/**
 * Google Analytics 4 (gtag.js) as an extra, best-effort sink next to the first-party
 * analytics_events pipeline (src/lib/analytics.js). Nothing here can throw into that pipeline:
 * every entry point is wrapped and is a no-op when gtag is not on the page.
 *
 * - The Measurement ID comes from GA_MEASUREMENT_ID (server/ga.mjs stamps the head bootstrap
 *   below into every portal and SEO page). Unset or malformed: no bootstrap, no gtag, nothing.
 * - Consent Mode v2: the bootstrap runs `consent default` BEFORE `js` / `config`: all four
 *   types denied in the EEA, the UK and Switzerland (region list below, waiting up to 500 ms
 *   for the consent message), granted elsewhere. A visitor who opted out of analytics
 *   (barumba.consent.analytics = "denied") or sends Global Privacy Control never loads gtag.
 * - installGaConsentSync() forwards the existing consent signals as `consent update`: the
 *   Google-certified consent message (TCF v2 __tcfapi, the same CMP the AdSense npa telemetry
 *   in ad-metrics.js reads) and the analytics opt-out key.
 * - gaEvent() maps a first-party event to its GA4 name and a small param whitelist; the
 *   tracker calls sendToGa() after it queues an event (so GA sees only what passed the
 *   first-party consent gate). Internal / qa=1 traffic carries traffic_type=internal.
 */

export const GA_ID = /^G-[A-Z0-9]{4,20}$/;
export const GA_SRC = "https://www.googletagmanager.com/gtag/js";
/** EU-27 + EEA (IS, LI, NO) + UK + Switzerland, ISO 3166-1 alpha-2 as gtag expects. */
export const GA_DENIED_REGIONS = Object.freeze([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  "IS", "LI", "NO",
  "GB", "CH",
]);
export const CONSENT_TYPES = Object.freeze(["ad_storage", "ad_user_data", "ad_personalization", "analytics_storage"]);
export const CONSENT_WAIT_MS = 500;
export const CMP_ABSENCE_WAIT_MS = 3000;
/** Google's IAB TCF vendor id. */
export const GOOGLE_TCF_VENDOR = 755;
const ANALYTICS_OPT_OUT_KEY = "barumba.consent.analytics";
/**
 * "1" once the server said this browser's session is internal (GET /api/me `internal`: the
 * same server verdict analytics_events uses: QA cookie, internal IP, non-browser UA or an
 * ANALYTICS_INTERNAL_EMAILS account). Read by the bootstrap so the next page's config, and so
 * its very first page_view, already carries traffic_type=internal.
 */
export const GA_INTERNAL_KEY = "barumba.ga_internal";

/** The configured Measurement ID, or "" (GA off). */
export function cleanGaId(value) {
  const id = String(value ?? "").trim().toUpperCase();
  return GA_ID.test(id) ? id : "";
}

const consentAll = (state) => Object.fromEntries(CONSENT_TYPES.map((key) => [key, state]));

/**
 * The page URL GA may see: an invite's /join/<code> (a bearer secret) becomes /join/_, and
 * the query keeps only campaign tags (utm_*, gclid), never challenge ids, refs or emails.
 * Kept ES2017-safe and dependency-free: gaBootstrap() below carries its own copy.
 */
export function gaPageLocation(loc) {
  try {
    const path = String(loc?.pathname || "/").replace(/^\/join\/[^?#]*/, "/join/_");
    const params = new URLSearchParams(loc?.search || "");
    const kept = new URLSearchParams();
    params.forEach((value, key) => { if (/^utm_[a-z_]{1,20}$/.test(key) || key === "gclid") kept.append(key, value.slice(0, 100)); });
    const query = kept.toString();
    return `${loc?.origin || ""}${path}${query ? `?${query}` : ""}`;
  } catch {
    return undefined;
  }
}

/**
 * The inline head bootstrap, serialized into the page by server/ga.mjs. ES2017-safe on
 * purpose (no ?. ?? or bare catch), like the AdSense head loader in site/render.js. Order
 * matters: consent defaults, then js + config, then the async gtag.js request.
 */
/* eslint-disable */
export function gaBootstrap(w, id, regions, wait) {
  var d = w.document, s = null, off = false, internal = false, client = false, q = null, path, kept = [], loc = w.location;
  try { off = w.navigator.globalPrivacyControl === true; } catch (e) {}
  try { s = w.localStorage; off = off || s.getItem("barumba.consent.analytics") === "denied"; } catch (e) {}
  // Establish defaults even while the CMP is pending: other Google tags may load first.
  if (!w.__barumbaGaDefaults) {
    w.dataLayer = w.dataLayer || [];
    w.gtag = w.gtag || function gtag() { w.dataLayer.push(arguments); };
    w.gtag("consent", "default", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied", region: regions, wait_for_update: wait });
    w.gtag("consent", "default", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "denied", analytics_storage: "granted" });
    w.__barumbaGaDefaults = true;
  }
  if (off) w.gtag("consent", "update", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
  if (w.__barumbaConsentPending) off = true;
  if (w.__barumbaGaTcfDenied) off = true;
  if (w.__barumbaConfig && w.__barumbaConfig.requiresConsent) {
    try { if (!s || s.getItem("barumba.consent.analytics") !== "granted") off = true; } catch (e) { off = true; }
  }
  if (off) {
    // Storage events do not fire in the document that made the choice.
    var retry = function () {
      if (w.__barumbaGa) return;
      try {
        var choice = null;
        try { if (s) choice = s.getItem("barumba.consent.analytics"); } catch (e) {}
        if (w.__barumbaConsentPending || w.__barumbaGaTcfDenied || w.navigator.globalPrivacyControl === true || choice === "denied" || (w.__barumbaConfig && w.__barumbaConfig.requiresConsent && choice !== "granted")) return;
        if (gaBootstrap(w, id, regions, wait)) {
          w.removeEventListener("storage", onStorage);
          w.removeEventListener("barumba-consent-grant", retry);
          if (typeof w.Event === "function") w.dispatchEvent(new w.Event("barumba-ga-ready"));
        }
      } catch (e) {}
    };
    var onStorage = function (event) { if (event.key === "barumba.consent.analytics") retry(); };
    try { w.addEventListener("storage", onStorage); } catch (e) {}
    try { w.addEventListener("barumba-consent-grant", retry); } catch (e) {}
    try {
      var proto = w.Storage.prototype, setItem = proto.setItem;
      proto.setItem = function (key, value) {
        var result = setItem.call(this, key, value);
        if (this === s && String(key) === "barumba.consent.analytics" && String(value) === "granted") retry();
        return result;
      };
    } catch (e) {}
    return false;
  }
  try { q = new URLSearchParams(loc.search).get("qa"); } catch (e) {}
  try { internal = w.navigator.webdriver === true || q === "1"; } catch (e) {}
  if (q !== "0") {
    try { internal = internal || s.getItem("bq_qa") === "1"; } catch (e) {}
    try { internal = internal || /(?:^|;\s*)bq_qa=1(?:;|$)/.test(String(d.cookie || "")); } catch (e) {}
  }
  // client = this browser's own QA/automation signals; the stored server verdict comes on top.
  client = internal;
  if (q !== "0") {
    try { internal = internal || s.getItem("barumba.ga_internal") === "1"; } catch (e) {}
    if (w.__barumbaGaInternalVerdict === true) internal = true;
  }
  try { if (s && s.getItem("barumba.consent.analytics") === "granted") w.gtag("consent", "update", { analytics_storage: "granted" }); } catch (e) {}
  w.gtag("js", new Date());
  var config = { send_page_view: false };
  try {
    path = String(loc.pathname || "/").replace(/^\/join\/[^?#]*/, "/join/_");
    new URLSearchParams(loc.search).forEach(function (v, k) { if (/^utm_[a-z_]{1,20}$/.test(k) || k === "gclid") kept.push(encodeURIComponent(k) + "=" + encodeURIComponent(v.slice(0, 100))); });
    config.page_location = loc.origin + path + (kept.length ? "?" + kept.join("&") : "");
  } catch (e) {}
  // GA would default page_referrer to document.referrer: same-site, that is the previous
  // page's raw URL (invite paths, challenge ids). Same-site → the same sanitizing as
  // page_location; another site → its origin only (all GA needs for the source).
  try {
    var ref = String(d.referrer || ""), r, rk = [];
    if (ref) {
      r = new URL(ref);
      if (r.origin === loc.origin) {
        r.searchParams.forEach(function (v, k) { if (/^utm_[a-z_]{1,20}$/.test(k) || k === "gclid") rk.push(encodeURIComponent(k) + "=" + encodeURIComponent(v.slice(0, 100))); });
        config.page_referrer = r.origin + String(r.pathname || "/").replace(/^\/join\/[^?#]*/, "/join/_") + (rk.length ? "?" + rk.join("&") : "");
      } else config.page_referrer = r.protocol + "//" + r.host + "/";
    }
  } catch (e) {}
  // Config-level: every hit of this config (page_view, our events, GA's own) carries it.
  if (internal) config.traffic_type = "internal";
  w.gtag("config", id, config);
  var el = d.createElement("script");
  el.async = true;
  el.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(id);
  d.head.appendChild(el);
  w.__barumbaGa = { id: id, internal: internal, client: client, page: config.page_location, ref: config.page_referrer };
  if (w.__barumbaGaAwaitVerdict && !internal) {
    w.__barumbaGa.held = [];
    w.__barumbaGa.timer = w.setTimeout(function () { w.dispatchEvent(new w.Event("barumba-ga-verdict-timeout")); }, 2000);
  }
  return true;
}
/* eslint-enable */

/** `<script>` text for the head: the bootstrap above, invoked with the configured ID. */
export function gaBootstrapSource(id) {
  const clean = cleanGaId(id);
  if (!clean) return "";
  return `(${gaBootstrap.toString()})(window, ${JSON.stringify(clean)}, ${JSON.stringify(GA_DENIED_REGIONS)}, ${CONSENT_WAIT_MS});`;
}

const pick = (src, keys) => {
  const out = {};
  for (const key of keys) {
    const value = src?.[key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") out[key] = value;
  }
  return out;
};
const AD_PARAMS = ["ad_network", "ad_placement", "ad_type", "ad_format", "ad_status", "filled", "reward_type", "reward_amount", "game_id", "ad_stub"];
/** GA4 reserves `ad_reward` (Analytics help 13316687), so that one event is renamed. */
export const GA_RENAMES = Object.freeze({ ad_reward: "ad_reward_granted" });
const gaMode = (ev) => (ev?.seed_mode === "daily" ? "daily" : "free_play");

/**
 * First-party event → `{ name, params }` for GA4, or null (not mirrored).
 *   session_start → page_view · game_start (game_id, mode) · game_end (game_id, mode, score, outcome)
 *   account_created → sign_up (method) · results_action{action:"save"} → save_tap · lead_tap (game_id)
 *   ad_* → same name (ad_reward → ad_reward_granted), ad params only.
 * Every mirrored event carries traffic_type=internal when the first-party event is internal.
 */
export function gaEvent(ev) {
  if (!ev || typeof ev.event !== "string") return null;
  const name = ev.event;
  let out = null;
  if (name === "session_start") out = { name: "page_view", params: {} };
  else if (name === "game_start") out = { name, params: { game_id: ev.game_id, mode: gaMode(ev) } };
  else if (name === "game_end") out = { name, params: { game_id: ev.game_id, mode: gaMode(ev), score: Number(ev.score) || 0, ...pick(ev, ["outcome"]) } };
  else if (name === "account_created") out = { name: "sign_up", params: pick(ev, ["method"]) };
  else if (name === "results_action" && ev.action === "save") out = { name: "save_tap", params: pick(ev, ["game_id", "variant", "placement"]) };
  else if (name === "lead_tap") out = { name, params: pick(ev, ["game_id"]) };
  else if (/^ad_[a-z_]+$/.test(name)) out = { name: GA_RENAMES[name] || name, params: pick(ev, AD_PARAMS) };
  if (!out) return null;
  for (const key of Object.keys(out.params)) if (out.params[key] === undefined) delete out.params[key];
  if (ev.is_internal === true) out.params.traffic_type = "internal";
  return out;
}

/**
 * From now on every GA hit on this page carries traffic_type=internal: `gtag('set')` is
 * page-wide, so it covers GA's own events too, not only the mirrored ones. Idempotent.
 */
export function markGaInternal(win = globalThis.window, { client = false } = {}) {
  try {
    if (!win || typeof win.gtag !== "function" || !win.__barumbaGa) return false;
    if (client) win.__barumbaGa.client = true;
    if (win.__barumbaGa.internal === true && win.__barumbaGa.set === true) return true;
    // After a clear (below) the config carries traffic_type:null, which beats `set`: re-config.
    if (win.__barumbaGa.cleared === true) reconfigGa(win, "internal");
    else win.gtag("set", { traffic_type: "internal" });
    win.__barumbaGa.internal = true;
    win.__barumbaGa.set = true;
    return true;
  } catch {
    return false;
  }
}

/** Re-issue this page's config (same cleaned page_location/page_referrer) with a new traffic_type. */
function reconfigGa(win, trafficType) {
  const ga = win.__barumbaGa;
  win.gtag("config", ga.id, {
    send_page_view: false,
    ...(ga.page ? { page_location: ga.page } : {}),
    ...(ga.ref ? { page_referrer: ga.ref } : {}),
    traffic_type: trafficType,
  });
  ga.cleared = trafficType === null;
}

/**
 * A non-internal server verdict on a page tagged only by the stored verdict (e.g. an internal
 * account signed out into an organic session): untag this page from now on. `gtag('set')` can't
 * undo a config-level value, but a config with `traffic_type: null` clears both (checked against
 * real gtag.js: later hits carry an empty `tt`). Pages tagged by this browser's own signals
 * (qa=1, the QA flag, webdriver, a QA-enrolled event) stay internal.
 */
export function clearGaInternal(win = globalThis.window) {
  try {
    const ga = win?.__barumbaGa;
    if (!ga || typeof win.gtag !== "function" || ga.internal !== true || ga.client === true) return false;
    reconfigGa(win, null);
    ga.internal = false;
    ga.set = false;
    return true;
  } catch {
    return false;
  }
}

/**
 * The server's verdict for this session (GET /api/me / POST /api/guest `internal`): persist
 * it for the next page's bootstrap and, when internal, tag this page's hits from now on.
 */
export function rememberGaInternal(internal, win = globalThis.window) {
  if (win && typeof internal === "boolean") win.__barumbaGaInternalVerdict = internal;
  let active = !!win?.__barumbaGa && typeof win.gtag === "function" && gaAnalyticsAllowed(win);
  try {
    const storage = win?.localStorage;
    active &&= storage?.getItem(ANALYTICS_OPT_OUT_KEY) !== "denied";
    // "0" is a known non-internal verdict too: that browser never waits again (holdGaForSession).
    if (active) storage?.setItem(GA_INTERNAL_KEY, internal === true ? "1" : "0");
  } catch { /* storage blocked: this page is still tagged below */ }
  const marked = active && (internal === true ? markGaInternal(win) : (clearGaInternal(win), false));
  releaseGa(win);
  return marked;
}

function gaAnalyticsAllowed(win) {
  let storage;
  try { storage = win?.localStorage; } catch { /* storage blocked */ }
  return analyticsAllowed({ nav: win?.navigator, storage, win, requiresConsent: win?.__barumbaConfig?.requiresConsent === true });
}

function discardGaQueued(win) {
  const ga = win?.__barumbaGa;
  ga?.pending?.clear();
  if (Array.isArray(ga?.held)) ga.held.length = 0;
}

/**
 * Pages without a session sync (the SEO pages): fetch the server verdict
 * (GET /api/ga-internal) when this browser has none stored, then apply it like the SPA does
 * (rememberGaInternal). Nothing waits for it: hits from then on are tagged page-wide and the
 * next page's config is tagged from its first hit. No-op without GA, after an opt-out, when
 * the page is already internal by its own signals, or when a verdict is stored.
 */
export function syncGaInternalVerdict(win = globalThis.window) {
  try {
    const ga = win?.__barumbaGa;
    if (!ga || typeof win.gtag !== "function" || typeof win.fetch !== "function") return null;
    if (!gaAnalyticsAllowed(win)) return null;
    if (ga.client === true) return null;
    let stored = null;
    try {
      if (win.localStorage?.getItem(ANALYTICS_OPT_OUT_KEY) === "denied") return null;
      stored = win.localStorage?.getItem(GA_INTERNAL_KEY) ?? null;
    } catch { /* storage blocked */ }
    if (stored === "1" || stored === "0") return null;
    return Promise.resolve(win.fetch("/api/ga-internal", { credentials: "same-origin", cache: "no-store" }))
      .then((res) => (res?.ok ? res.json() : null))
      .then((body) => (body && typeof body.internal === "boolean" ? rememberGaInternal(body.internal, win) : null))
      .catch(() => null);
  } catch {
    return null;
  }
}

/** Mirror one first-party event to GA4. Never throws; no-op without gtag. */
export function sendToGa(ev, win = globalThis.window) {
  try {
    if (!win || typeof win.gtag !== "function" || !win.__barumbaGa) return false;
    // A session that turned internal mid-page (QA enrolment from another tab, webdriver):
    // tag the whole page, not just this event.
    if (ev?.is_internal === true) markGaInternal(win, { client: true });
    const mapped = gaEvent(ev);
    if (!mapped) return false;
    const ga = win.__barumbaGa;
    const id = typeof ev.event_id === "string" && ev.event_id ? ev.event_id : null;
    const page = gaPageLocation(win.location);
    if (mapped.name === "page_view" && page) ga.lastPage = page;
    const args = ["event", mapped.name, { ...mapped.params, ...(page ? { page_location: page } : {}) }];
    // The run tracker's provisional end on a backgrounded tab (game_end quit/hidden) reuses
    // the run's end event_id so the server can replace it if the player comes back. GA can't
    // replace a hit: keep it pending, drop it when the settled end (same id) arrives, send it
    // when the page really unloads (pagehide, not persisted). Each event_id reaches GA at most once.
    if (id && ga.pending?.has(id)) ga.pending.delete(id);
    if (id && ga.sent?.has(id)) return false;
    if (mapped.name === "game_end" && ev.outcome === "quit" && ev.end_trigger === "hidden" && id) {
      if (!ga.pending) {
        ga.pending = new Map();
        // Only a real unload finalizes it. `freeze` and a bfcache `pagehide` (persisted) can
        // resume and finish the run, so they don't (a tab discarded while frozen/cached then
        // loses that end in GA; analytics_events still has it).
        const flush = (event) => {
          if (event?.persisted === true) return;
          if (!gaAnalyticsAllowed(win)) { discardGaQueued(win); return; }
          for (const [key, held] of ga.pending) { ga.pending.delete(key); emitGa(win, key, held); }
        };
        win.addEventListener?.("pagehide", flush);
      }
      ga.pending.set(id, args);
      return true;
    }
    emitGa(win, id, args);
    return true;
  } catch {
    return false;
  }
}

function emitGa(win, id, args) {
  try {
    const ga = win.__barumbaGa;
    if (id) (ga.sent ||= new Set()).add(id);
    // Waiting for the session's internal verdict (holdGaForSession): queued, sent in order.
    if (Array.isArray(ga.held)) ga.held.push(args);
    else win.gtag(...args);
  } catch { /* GA is optional */ }
}

/**
 * Manual GA page_view for an in-app route change (src/lib/router.js: pushState navigation and
 * back/forward). GA's own history-change page_views are off in the stream (they would carry
 * the raw URL), so this is the only route page_view: page_location and page_referrer (the
 * previous in-app page) get the same cleaning as everywhere else. Skipped when the cleaned
 * location didn't change, so the initial load (session_start's page_view) and query-only
 * changes never double-count; skipped after a mid-page analytics opt-out. Internal pages
 * stay tagged (config + `set`, and the param itself). Never throws; no-op without GA.
 */
export function gaRoutePageView(win = globalThis.window) {
  try {
    const ga = win?.__barumbaGa;
    if (!ga || typeof win.gtag !== "function") return false;
    try { if (!analyticsAllowed({ nav: win.navigator, storage: win.localStorage, win })) return false; } catch { /* storage blocked */ }
    if (win.navigator?.globalPrivacyControl === true) return false;
    const page = gaPageLocation(win.location);
    if (!page || page === ga.lastPage) return false;
    const referrer = ga.lastPage || null;
    ga.lastPage = page;
    const params = { page_location: page, ...(referrer ? { page_referrer: referrer } : {}), ...(ga.internal === true ? { traffic_type: "internal" } : {}) };
    emitGa(win, null, ["event", "page_view", params]);
    return true;
  } catch {
    return false;
  }
}

/** Send the held events (after the verdict, or on timeout). Idempotent. */
export function releaseGa(win = globalThis.window) {
  try {
    const held = win?.__barumbaGa?.held;
    if (!Array.isArray(held)) return false;
    win.__barumbaGa.held = null;
    if (win.__barumbaGa.timer && win.clearTimeout) win.clearTimeout(win.__barumbaGa.timer);
    if (!gaAnalyticsAllowed(win)) { discardGaQueued(win); return false; }
    for (const args of held) { try { win.gtag(...args); } catch { /* next */ } }
    return true;
  } catch {
    return false;
  }
}

/**
 * The SPA's first page_view fires before the session sync answers, so a signed-in internal
 * account (ANALYTICS_INTERNAL_EMAILS) on a browser without a stored verdict would send it
 * untagged. Only then (`signedInMember` and no stored "1"/"0" verdict) mirrored events wait
 * for rememberGaInternal() (the /api/guest answer) or `ms`, whichever is first. Everyone
 * else (fresh browsers, guests, any browser with a verdict, already-internal pages) never
 * waits. Returns whether it is holding.
 */
export function holdGaForSession(win = globalThis.window, { signedInMember = false, ms = 2000 } = {}) {
  try {
    const ga = win?.__barumbaGa;
    if (!ga || typeof win.gtag !== "function" || ga.internal === true || Array.isArray(ga.held)) return false;
    if (signedInMember !== true) return false;
    let verdict = null;
    try { verdict = win.localStorage?.getItem(GA_INTERNAL_KEY) ?? null; } catch { /* blocked */ }
    if (verdict === "1" || verdict === "0") return false;
    ga.held = [];
    ga.timer = win.setTimeout ? win.setTimeout(() => releaseGa(win), ms) : null;
    // A bounce inside the window still sends its page_view.
    const flush = () => releaseGa(win);
    win.addEventListener?.("pagehide", flush, { once: true });
    win.document?.addEventListener?.("visibilitychange", () => { if (win.document.visibilityState === "hidden") flush(); });
    return true;
  } catch {
    return false;
  }
}

/**
 * TCF v2 data from the consent message → a Consent Mode v2 update, or null when there is
 * nothing to say yet (prompt still up, GDPR does not apply). Google's documented mapping:
 * ad_storage = purpose 1, ad_user_data = purposes 1 + 7, ad_personalization = purposes 3 + 4;
 * analytics_storage requires purposes 1 and 8 (storage and measurement). All also need Google's vendor consent (755).
 */
export function consentFromTcf(data) {
  if (!data || typeof data !== "object") return null;
  if (!["tcloaded", "useractioncomplete"].includes(data.eventStatus)) return null;
  if (data.gdprApplies !== true) return null;
  const p = data.purpose?.consents || {};
  const vendor = data.vendor?.consents?.[GOOGLE_TCF_VENDOR] === true;
  const g = (ok) => (ok && vendor ? "granted" : "denied");
  return {
    ad_storage: g(p[1] === true),
    ad_user_data: g(p[1] === true && p[7] === true),
    ad_personalization: g(p[3] === true && p[4] === true),
    analytics_storage: g(p[1] === true && p[8] === true),
  };
}

/** `gtag('consent','update', …)`. Never throws; no-op without gtag. */
export function updateGaConsent(win, consent) {
  try {
    if (!consent || typeof win?.gtag !== "function") return false;
    win.gtag("consent", "update", consent);
    return true;
  } catch {
    return false;
  }
}

// EEA/UK/CH, EU outermost regions (and all Europe/*, for simplicity; list in consent-geo.js):
// Google's CMP loads and should answer there (server/funding-choices.mjs uses the same check),
// so these visitors are held denied until it does instead of taking the no-CMP fallback.
export function consentHoldZone(win = globalThis.window) {
  let tz = "";
  try { tz = win?.Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* unknown */ }
  return isConsentHoldTimeZone(tz);
}

/**
 * Forward the site's consent signals to gtag as `consent update`:
 *  - the consent message (TCF v2 `__tcfapi`, injected by the AdSense loader, so it may appear
 *    after this runs: polled once a second, optionally bounded by `maxPolls`);
 *  - the first-party analytics opt-out (`barumba.consent.analytics` = "denied", any tab).
 * Returns a stop function. No-op when GA is not on the page.
 */
export function installGaConsentSync({ win = globalThis.window, pollMs = 1000, maxPolls = Infinity } = {}) {
  const offs = [];
  try {
    if (!win) return () => {};
    let last = "";
    const optedOut = () => {
      try { return win.localStorage?.getItem(ANALYTICS_OPT_OUT_KEY) === "denied"; } catch { return false; }
    };
    const apply = (consent) => {
      if (consent?.analytics_storage === "denied") discardGaQueued(win);
      const key = JSON.stringify(consent);
      if (!consent || key === last) return;
      last = key;
      updateGaConsent(win, consent);
    };
    let polls = 0;
    let timer = null;
    let previousPurposeGrant = false;
    // Production's Google CMP is absent outside its EU/UK message regions. Only fall back
    // when no CMP exists at the deadline; a present but silent CMP remains pending.
    const settleWithoutCmp = () => {
      if (!win.__barumbaConsentPending) return;
      if (consentHoldZone(win)) {
        win.__barumbaReducedConsent = "pending";
        win.dispatchEvent?.(new win.Event("barumba-consent-change"));
        return;
      }
      if (typeof win.__tcfapi === "function") return;
      win.__barumbaConsentPending = false;
      win.__barumbaConfig = { ...win.__barumbaConfig, requiresConsent: false };
      win.dispatchEvent?.(new win.Event("barumba-consent-change"));
    };
    const flushOnExit = () => win.__barumbaErrors?.tracker?.flush({ beacon: true });
    win.addEventListener?.("pagehide", flushOnExit);
    offs.push(() => win.removeEventListener?.("pagehide", flushOnExit));
    const attach = () => {
      timer = null;
      if (typeof win.__tcfapi === "function") {
        // The first-party opt-out stays authoritative: a later CMP answer never re-grants.
        try { win.__tcfapi("addEventListener", 2, (data, ok) => {
          if (!ok || !data) return;
          if (data.eventStatus === "cmpuishown") win.__barumbaCmpPromptSeen = true;
          if (data.tcString || (typeof data.gdprApplies === "boolean" && ["tcloaded", "useractioncomplete"].includes(data.eventStatus))) win.__barumbaCmpAnswered = true;
          if (typeof data.gdprApplies !== "boolean") return;
          if (data.gdprApplies && !["tcloaded", "useractioncomplete"].includes(data.eventStatus)) return;
          win.__barumbaConsentPending = false;
          win.__barumbaConfig = { ...win.__barumbaConfig, requiresConsent: data.gdprApplies };
          // Set the full Google verdict before writing first-party consent: that write can
          // synchronously trigger the GA bootstrap and consent-change listeners.
          const allowed = data.purpose?.consents?.[1] === true && data.purpose?.consents?.[8] === true;
          win.__barumbaGaTcfDenied = data.gdprApplies && (!allowed || data.vendor?.consents?.[GOOGLE_TCF_VENDOR] !== true);
          win.__barumbaReducedConsent = win.__barumbaGaTcfDenied ? "denied" : null;
          if (data.gdprApplies && !win.__barumbaGaTcfDenied && win.navigator?.globalPrivacyControl !== true) {
            try {
              const choice = win.localStorage?.getItem(ANALYTICS_OPT_OUT_KEY);
              const source = win.localStorage?.getItem("barumba.consent.analytics.source");
              // Only a withdrawal of a CMP grant may replace an explicit first-party grant.
              if ((choice !== "denied" || source === "cmp") && (allowed ? choice !== "granted" : choice === "granted" && (source === "cmp" || previousPurposeGrant))) {
                win.__barumbaWritingCmpConsent = true;
                try {
                  win.localStorage?.setItem("barumba.consent.analytics.source", "cmp");
                  win.localStorage?.setItem(ANALYTICS_OPT_OUT_KEY, allowed ? "granted" : "denied");
                } finally { win.__barumbaWritingCmpConsent = false; }
              }
            } catch { /* storage unavailable */ }
          }
          previousPurposeGrant = data.gdprApplies && allowed;
          apply(optedOut() || win.navigator?.globalPrivacyControl === true ? consentAll("denied") : consentFromTcf(data));
          win.dispatchEvent?.(new win.Event("barumba-consent-change"));
          if (!optedOut() && win.navigator?.globalPrivacyControl !== true && !win.__barumbaGaTcfDenied) {
            win.dispatchEvent?.(new win.Event("barumba-consent-grant"));
          }
        }); } catch { /* unsupported CMP */ }
        return;
      }
      polls += 1;
      if (polls < maxPolls && win.setTimeout) timer = win.setTimeout(attach, pollMs);
    };
    if (win.__barumbaConsentPending && win.setTimeout) timer = win.setTimeout(attach, 0);
    else attach();
    let fallback = null;
    const startFallback = () => { if (win.__barumbaConsentPending && win.setTimeout) fallback = win.setTimeout(settleWithoutCmp, CMP_ABSENCE_WAIT_MS); };
    if (consentHoldZone(win) && win.document && win.document.readyState !== "complete") {
      win.addEventListener?.("load", startFallback, { once: true });
      offs.push(() => win.removeEventListener?.("load", startFallback));
    } else startFallback();
    offs.push(() => { if (timer && win.clearTimeout) win.clearTimeout(timer); });
    offs.push(() => { if (fallback && win.clearTimeout) win.clearTimeout(fallback); });
    const onStorage = (event) => {
      if (event?.key === ANALYTICS_OPT_OUT_KEY && event.newValue === "denied") { gaAnalyticsAllowed(win); apply(consentAll("denied")); }
      if (event?.key === ANALYTICS_OPT_OUT_KEY && event.newValue === "granted") {
        if (win.navigator?.globalPrivacyControl === true || win.__barumbaGaTcfDenied) apply(consentAll("denied"));
        else if (!win.__barumbaConsentPending) apply({ analytics_storage: "granted" });
      }
    };
    const onLocalChange = () => {
      let choice;
      try { choice = win.localStorage?.getItem(ANALYTICS_OPT_OUT_KEY); } catch { return; }
      onStorage({ key: ANALYTICS_OPT_OUT_KEY, newValue: choice });
    };
    win.addEventListener?.("storage", onStorage);
    win.addEventListener?.("barumba-consent-change", onLocalChange);
    offs.push(() => win.removeEventListener?.("storage", onStorage));
    offs.push(() => win.removeEventListener?.("barumba-consent-change", onLocalChange));
  } catch { /* GA is optional */ }
  return () => { for (const off of offs.splice(0)) { try { off(); } catch { /* gone */ } } };
}
